import React, { useState } from 'react';
import { Navigation, Loader2, AlertCircle } from 'lucide-react';
import api from '@/lib/api';

/**
 * DistanceToClinic — one-tap travel-time widget.
 *
 * UX:
 *   1. Initial state: amber pill button "Show travel time from my location"
 *   2. On click: requests browser geolocation (HTTPS-only, patient permission)
 *   3. POSTs {location_id, patient_lat, patient_lon} to /api/public/clinic-distance
 *   4. Renders "🚗 12.4 km · ~24 min from your location" + the source badge
 *      (Google/OSRM/straight-line) so we're transparent about data quality.
 *
 * Privacy:
 *   - Geolocation never persisted server-side; backend uses it once for the
 *     OSRM/Google routing call and discards.
 *   - Falls back to a clear "Allow location to see travel time" if the patient
 *     denies the permission prompt.
 */
export default function DistanceToClinic({ locationId, accentColor = '#0EA5E9' }) {
  const [state, setState] = useState('idle'); // idle | loading | done | denied | error
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const ask = () => {
    if (!('geolocation' in navigator)) {
      setState('error');
      setErrorMsg('Your browser does not support location.');
      return;
    }
    setState('loading');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api.post('/public/clinic-distance', {
            location_id: locationId,
            patient_lat: pos.coords.latitude,
            patient_lon: pos.coords.longitude,
          });
          setData(r.data);
          setState('done');
        } catch (e) {
          setState('error');
          setErrorMsg(e?.response?.data?.detail || 'Could not compute distance.');
        }
      },
      (err) => {
        setState(err.code === err.PERMISSION_DENIED ? 'denied' : 'error');
        setErrorMsg(err.code === err.PERMISSION_DENIED
          ? 'Allow location access to see travel time.'
          : 'Could not read your location.');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  };

  if (state === 'done' && data) {
    const { distance_km, duration_min } = data;
    return (
      <div className="inline-flex items-center gap-3 rounded-xl border bg-white px-4 py-2.5 mt-3 shadow-sm"
           style={{ borderColor: accentColor }}
           data-testid="distance-result">
        <Navigation size={16} style={{ color: accentColor }} className="shrink-0" />
        <div className="leading-tight">
          <p className="text-base font-bold text-slate-900 tabular-nums">
            {distance_km} km {duration_min > 0 && <span className="text-slate-500 font-medium">· ~{duration_min} min</span>}
          </p>
          <p className="text-[11px] text-slate-400 font-medium">From your location</p>
        </div>
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <div className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-50 px-4 py-2 mt-3 text-sm text-slate-600 font-medium" data-testid="distance-loading">
        <Loader2 size={14} className="animate-spin" />
        Reading your location…
      </div>
    );
  }

  if (state === 'denied' || state === 'error') {
    return (
      <button onClick={ask}
              className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 mt-3 text-sm font-semibold text-amber-800 bg-amber-50 border-amber-300 hover:bg-amber-100 transition-colors"
              data-testid="distance-error-retry"
              title={errorMsg}>
        <AlertCircle size={14} />
        {errorMsg} Tap to retry.
      </button>
    );
  }

  return (
    <button onClick={ask}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 mt-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: accentColor }}
            data-testid="distance-cta">
      <Navigation size={14} />
      Show travel time from my location
    </button>
  );
}
